import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Thermometer } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './chartTheme';

function MiniLine({ data, lines, yLabel, decimals = 1 }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yLabel} vs SoC`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${lines[0].key}`} />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={data} margin={{ top: 4, right: 12, left: 4, bottom: 20 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="soc_pct" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'SoC (%)', position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }}
            tickFormatter={(v) => Number(v).toFixed(0)}
          />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={(v) => `SoC: ${Number(v).toFixed(1)}%`}
            formatter={(v) => Number(v).toFixed(decimals)}
          />
          {lines.map((l) => <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} {...LINE_PROPS} />)}
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Pack/coolant temperature and cooling/heating electrical draw across the selected charge —
 * the thermal-management behavior added when the CC-CV model gained a two-node pack/coolant
 * thermal model with hysteretic cooling/heating (replacing the earlier ambient-only
 * temperature derate). Clipped to Start SoC -> Target SoC, same convention as
 * ChargingCurveChart.
 */
const ChargingThermalChart = ({ result, startSoc, targetSoc }) => {
  const data = useMemo(() => {
    if (!result) return [];
    const lo = startSoc - 0.05;
    const hi = targetSoc + 0.05;
    return result.series_soc_pct
      .map((soc, i) => ({
        soc_pct: soc,
        pack_temp_c: result.series_pack_temp_c[i],
        coolant_temp_c: result.series_coolant_temp_c[i],
      }))
      .filter((d) => d.soc_pct >= lo && d.soc_pct <= hi);
  }, [result, startSoc, targetSoc]);

  if (data.length === 0) return null;

  return (
    <ChartCard
      icon={Thermometer}
      title="Thermal Behavior vs SoC"
      description="Pack and coolant temperature across the selected charge, driven by the two-node thermal model's cooling/heating hysteresis."
      testId="chart-charging-thermal"
    >
      <div className="grid sm:grid-cols-1 gap-4">
        <MiniLine
          data={data}
          lines={[
            { key: 'pack_temp_c', name: 'Pack Temp (°C)', color: SERIES_COLORS[1] },
            { key: 'coolant_temp_c', name: 'Coolant Temp (°C)', color: SERIES_COLORS[0] },
          ]}
          yLabel="Temperature (°C)"
          decimals={1}
        />
      </div>
    </ChartCard>
  );
};

export default ChargingThermalChart;
